import { useCallback, useEffect, useState } from "react";
import * as PDFJS from "pdfjs-dist/legacy/build/pdf";
import { PDFDocument } from "pdf-lib";
import { getSharedPdfWorker } from "../pdfDocumentCache";
import { applyCopyProtection } from "./pdfProtection";
import AWSService from "../../../../../services/AWSService";

// A redaction box is stored in page-relative units (0..1) so it lines up at
// any zoom level: { id, page, x, y, w, h }.
//
// Redactions are kept per file URL, so the same file opened from the core
// bundle list, the viewer or a trial bundle shares one set of boxes.

const STORAGE_PREFIX = "doc_redactions_";
const EXPORT_SCALE = 2; // ~144 DPI, sharp enough for court documents
const REDACTED_CHAR = "█";

export const redactionKey = (url = "") =>
  typeof url === "string" ? url.split("?")[0] : "";

let _nextId = Date.now();
export const redactionUid = () => `r${_nextId++}`;

// ─── Store ───────────────────────────────────────────────────────────────────

const listeners = new Map(); // key -> Set<fn>

export function getRedactions(url) {
  const key = redactionKey(url);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export function setRedactions(url, list) {
  const key = redactionKey(url);
  if (!key) return;
  try {
    if (list && list.length) {
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(list));
    } else {
      localStorage.removeItem(STORAGE_PREFIX + key);
    }
  } catch {}
  (listeners.get(key) || []).forEach((fn) => fn());
}

export const hasRedactions = (url) => getRedactions(url).length > 0;

function subscribe(key, fn) {
  if (!listeners.has(key)) listeners.set(key, new Set());
  listeners.get(key).add(fn);
  return () => listeners.get(key)?.delete(fn);
}

// The viewer can be opened in a second tab; keep both tabs in sync.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (!e.key || !e.key.startsWith(STORAGE_PREFIX)) return;
    const key = e.key.slice(STORAGE_PREFIX.length);
    (listeners.get(key) || []).forEach((fn) => fn());
  });
}

export function useRedactions(url) {
  const key = redactionKey(url);
  const [list, setList] = useState(() => getRedactions(url));

  useEffect(() => {
    setList(getRedactions(key));
    return subscribe(key, () => setList(getRedactions(key)));
  }, [key]);

  const update = useCallback(
    (next) =>
      setRedactions(
        key,
        typeof next === "function" ? next(getRedactions(key)) : next,
      ),
    [key],
  );

  return [list, update];
}

// ─── Geometry ────────────────────────────────────────────────────────────────

// Turns rects measured on screen (relative to the page canvas) into
// page-relative redaction boxes.
export function rectsToRedactions(rects, page, pageWidth, pageHeight) {
  if (!pageWidth || !pageHeight) return [];
  return (rects || [])
    .filter((r) => r.width > 0 && r.height > 0)
    .map((r) => ({
      id: redactionUid(),
      page,
      x: r.left / pageWidth,
      y: r.top / pageHeight,
      w: r.width / pageWidth,
      h: r.height / pageHeight,
    }));
}

// ─── Text layer guard ────────────────────────────────────────────────────────

// Returns a react-pdf `customTextRenderer` that replaces every character
// lying under a redaction box, so redacted text can't be selected, copied or
// found with Ctrl+F in the viewer.
export function makeRedactedTextRenderer(boxes) {
  return (item) => {
    const str = item.str || "";
    const page = item.page;
    if (!str || !page || !boxes.length) return str;

    // Rotated pages: box coordinates are in the rotated frame, so hide all
    // text on the page rather than risk leaking any of it.
    if ((page.rotate || 0) % 360 !== 0) return str.replace(/\S/g, REDACTED_CHAR);

    const [xMin, yMin, xMax, yMax] = page.view;
    const pageW = xMax - xMin;
    const pageH = yMax - yMin;
    const t = item.transform || [];
    const fontH = Math.abs(t[3]) || Math.hypot(t[2] || 0, t[3] || 0) || 1;
    const x0 = (t[4] - xMin) / pageW;
    const x1 = x0 + (item.width || 0) / pageW;
    const yTop = (yMax - (t[5] + fontH)) / pageH;
    const yBot = (yMax - t[5] + fontH * 0.25) / pageH;
    const lineH = yBot - yTop;

    const lineBoxes = boxes.filter((b) => {
      const overlap = Math.min(yBot, b.y + b.h) - Math.max(yTop, b.y);
      return overlap > lineH * 0.3;
    });
    if (!lineBoxes.length) return str;

    const cw = (x1 - x0) / str.length;
    let out = "";
    for (let i = 0; i < str.length; i++) {
      const c0 = x0 + (i + 0.2) * cw;
      const c1 = x0 + (i + 0.8) * cw;
      const hidden = lineBoxes.some((b) => c1 > b.x && c0 < b.x + b.w);
      out += hidden && str[i] !== " " ? REDACTED_CHAR : str[i];
    }
    return out;
  };
}

// ─── Redacted PDF export ─────────────────────────────────────────────────────

async function toBytes(source) {
  if (source instanceof Uint8Array) return source;
  if (source instanceof ArrayBuffer) return new Uint8Array(source);
  if (typeof Blob !== "undefined" && source instanceof Blob) {
    return new Uint8Array(await source.arrayBuffer());
  }
  const res = await fetch(source);
  if (!res.ok) throw new Error(`Could not fetch the document (${res.status})`);
  return new Uint8Array(await res.arrayBuffer());
}

export function openPdfForRedaction(bytes) {
  const worker = getSharedPdfWorker();
  // pdf.js transfers the buffer to its worker, so give it a copy.
  return PDFJS.getDocument({
    data: bytes.slice(),
    isEvalSupported: false,
    ...(worker ? { worker } : {}),
  });
}

const canvasToJpeg = (canvas) =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob
          ? blob.arrayBuffer().then(resolve, reject)
          : reject(new Error("Could not encode page image")),
      "image/jpeg",
      0.92,
    ),
  );

/**
 * Builds a truly redacted PDF. Once a document has any redaction, every page
 * is flattened to an image (with the black boxes burned in on the redacted
 * pages), so the file has no text layer at all and nothing in it can be
 * selected or copied. The hidden text, fonts and annotations no longer exist
 * in the file, and the original file's metadata is not carried over. The
 * flattened file is also copy-protected (see pdfProtection.js), so a viewer
 * that OCRs the page images still can't copy the text.
 *
 * @param source  URL, Blob/File, ArrayBuffer or Uint8Array of the PDF
 * @param boxes   redaction boxes
 * @returns Blob (application/pdf)
 */
export async function buildRedactedPdf(source, boxes, { onProgress } = {}) {
  const bytes = await toBytes(source);
  const byPage = new Map();
  (boxes || []).forEach((b) => {
    if (!byPage.has(b.page)) byPage.set(b.page, []);
    byPage.get(b.page).push(b);
  });

  const task = openPdfForRedaction(bytes);
  const pdf = await task.promise;

  // With no redactions at all, copy the pages from the original when pdf-lib
  // can read it (e.g. not encrypted). Otherwise flatten every page so no
  // copyable text is left anywhere in the document.
  let src = null;
  if (!byPage.size) {
    try {
      src = await PDFDocument.load(bytes, { ignoreEncryption: true });
      if (src.isEncrypted) src = null;
    } catch {
      src = null;
    }
  }

  const out = await PDFDocument.create();
  out.setProducer("JustAct");
  out.setCreator("JustAct");

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  try {
    for (let p = 1; p <= pdf.numPages; p++) {
      const pageBoxes = byPage.get(p) || [];
      if (src) {
        const [copied] = await out.copyPages(src, [p - 1]);
        out.addPage(copied);
      } else {
        const page = await pdf.getPage(p);
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: EXPORT_SCALE });
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvasContext: ctx, viewport }).promise;

        ctx.fillStyle = "#000000";
        pageBoxes.forEach((b) => {
          ctx.fillRect(
            Math.floor(b.x * canvas.width),
            Math.floor(b.y * canvas.height),
            Math.ceil(b.w * canvas.width) + 1,
            Math.ceil(b.h * canvas.height) + 1,
          );
        });

        const jpg = await out.embedJpg(await canvasToJpeg(canvas));
        const newPage = out.addPage([base.width, base.height]);
        newPage.drawImage(jpg, {
          x: 0,
          y: 0,
          width: base.width,
          height: base.height,
        });
        page.cleanup();
      }
      if (onProgress) onProgress(p, pdf.numPages);
    }
  } finally {
    canvas.width = 0;
    canvas.height = 0;
    task.destroy();
  }

  // Flattened pages have no text, but PDF viewers OCR images; lock copying
  // so that recognised text can't be selected or copied either.
  if (!src) {
    await out.flush();
    applyCopyProtection(out);
  }
  const outBytes = await out.save({ useObjectStreams: !!src });
  return new Blob([outBytes], { type: "application/pdf" });
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export const redactedFileName = (name = "document") =>
  `${String(name)
    .replace(/\.[^.]+$/, "")
    .replace(/[\\/:*?"<>|]+/g, "_")}_redacted.pdf`;

export function downloadBlob(blob, fileName) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

// Uploads a redacted PDF to S3 and returns its public URL.
export async function uploadRedactedPdf(blob, fileName) {
  const file = new File([blob], fileName, { type: "application/pdf" });
  const s3Response = await AWSService.getS3URL({ key: [file.name] });
  const putUrl = Array.isArray(s3Response?.url)
    ? s3Response.url[0]
    : s3Response?.url;
  if (!putUrl) throw new Error("Could not get an upload URL");
  return AWSService.uploadToS3(putUrl, file);
}
